import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type Spread,
  DecoratorNode,
  LexicalEditor,
  isHTMLElement,
  SerializedLexicalNode,
} from 'lexical'
import { CrewEntityReference } from 'components/elements/crewEntityReference/crewEntityReference'
import { EntityType } from '@crew/enums/domain'
import { entityReference as entityReferenceConst } from '@crew/configs/constants'

// シリアライズ用型の定義
// editorState.toJSON() で EntityReferenceNode がどう出てくるかの型
export type SerializedEntityReferenceNode = Spread<
  {
    type: typeof entityReferenceConst.CLASS_NAME
    entityType: EntityType
    entityRecordId: string
    entityName: string
  },
  SerializedLexicalNode
>

// EntityReferenceNode クラスの実装
export class EntityReferenceNode extends DecoratorNode<JSX.Element> {
  __entityType: EntityType
  __entityRecordId: string
  __entityName: string

  constructor(
    entityType: EntityType,
    entityRecordId: string,
    entityName: string,
    key?: NodeKey
  ) {
    super(key)
    this.__entityType = entityType
    this.__entityRecordId = entityRecordId
    this.__entityName = entityName
  }

  // ノードの種類を返すメソッド
  static getType(): string {
    return entityReferenceConst.CLASS_NAME
  }

  // ノードを複製するメソッド
  static clone(node: EntityReferenceNode): EntityReferenceNode {
    return new EntityReferenceNode(
      node.__entityType,
      node.__entityRecordId,
      node.__entityName,
      node.__key
    )
  }

  // JSON からノードを復元するためのメソッド
  static importJSON(
    serializedNode: SerializedEntityReferenceNode
  ): EntityReferenceNode {
    // text プロパティは SerializedTextNode 由来と仮定
    const node = new EntityReferenceNode(
      serializedNode.entityType,
      serializedNode.entityRecordId,
      serializedNode.entityName
    )
    return node
  }

  // シリアライズ処理
  exportJSON(): SerializedEntityReferenceNode {
    return {
      ...super.exportJSON(),
      entityType: this.__entityType,
      entityRecordId: this.__entityRecordId,
      entityName: this.__entityName,
    } as SerializedEntityReferenceNode
  }

  // ノードの DOM 表現を作成するメソッド
  createDOM(config: EditorConfig): HTMLElement {
    const dom = document.createElement('span')
    if (config.theme.reference) dom.className = config.theme.reference
    return dom
  }

  updateDOM(): false {
    return false
  }

  // DOM 出力など必要に応じたメソッド（例として placeholder）
  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const { element } = super.exportDOM(editor)
    if (element && isHTMLElement(element)) {
      element.setAttribute(
        entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.NAME,
        entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.VALUE
      )
      element.setAttribute(
        entityReferenceConst.DATA_ATTRIBUTE.ENTITY_TYPE.NAME,
        this.__entityType
      )
      element.setAttribute(
        entityReferenceConst.DATA_ATTRIBUTE.ENTITY_RECORD_ID.NAME,
        this.__entityRecordId
      )
      element.textContent = this.__entityName
    }

    return { element }
  }

  // DOM から ReferenceNode への変換マップを提供するメソッド
  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (
          domNode.hasAttribute(
            entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.NAME
          )
        ) {
          return {
            conversion: $convertEntityReferenceElement,
            // 他の変換よりもこちらを優先する
            // prioが1だと、extendedTextNodeに変換されてしまう
            priority: 2,
          }
        }
        return null
      },
      // CrewHtmlContent（編集時でもないチャットメッセージの表示など）で、<span data-tag-type="reference" data-entity-type="Event" data-entity-record-id="123">が、<a>タグに変換されるため、それのペーストに対応するためにaタグの変換も追加する
      a: (domNode: HTMLElement) => {
        if (
          domNode.hasAttribute(
            entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.NAME
          )
        ) {
          return {
            conversion: $convertEntityReferenceElement,
            priority: 2,
          }
        }
        return null
      },
    }
  }

  decorate(): JSX.Element {
    return (
      <CrewEntityReference
        entityType={this.__entityType}
        entityRecordId={this.__entityRecordId}
      />
    )
  }

  getTextContent(): string {
    return this.__entityName
  }
}

// DOM 要素を EntityReferenceNode に変換する関数
function $convertEntityReferenceElement(
  domNode: HTMLElement
): DOMConversionOutput | null {
  if (
    !domNode.hasAttribute(entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.NAME)
  ) {
    return null
  }
  if (
    !domNode.hasAttribute(entityReferenceConst.DATA_ATTRIBUTE.ENTITY_TYPE.NAME)
  ) {
    return null
  }
  if (
    !domNode.hasAttribute(
      entityReferenceConst.DATA_ATTRIBUTE.ENTITY_RECORD_ID.NAME
    )
  ) {
    return null
  }

  const entityType = domNode.getAttribute(
    entityReferenceConst.DATA_ATTRIBUTE.ENTITY_TYPE.NAME
  ) as EntityType
  const entityRecordId = domNode.getAttribute(
    entityReferenceConst.DATA_ATTRIBUTE.ENTITY_RECORD_ID.NAME
  )
  const entityName = domNode.textContent ?? ''

  if (entityType && entityRecordId) {
    const node = $createEntityReferenceNode(
      entityType,
      entityRecordId,
      entityName
    )
    return { node }
  }

  return null
}

// 新しい ReferenceNode を作成する関数
export function $createEntityReferenceNode(
  entityType: EntityType,
  entityRecordId: string,
  entityName: string = `!${entityType}:${entityRecordId}`
): EntityReferenceNode {
  const referenceNode = new EntityReferenceNode(
    entityType,
    entityRecordId,
    entityName
  )
  return $applyNodeReplacement(referenceNode)
}

// 与えられたノードが EntityReferenceNode かどうかを判定する関数
export function $isEntityReferenceNode(
  node: LexicalNode | null | undefined
): node is EntityReferenceNode {
  return node instanceof EntityReferenceNode
}
